import { fg } from '@confluence/feature-gating';

import type {
	IssueCreateSidePanelActions,
	PageMode,
} from '../providers/IssueCreateSidePanelContextProvider';
import {
	constructInsertMultipleStorageFragmentsRequestPayload,
	constructInsertStorageColumnTableRequestPayload,
	getValidRange,
	getValidRangeForActionItem,
} from '../services/issueInsertionUtils';

import { getClosestActionListFromSelection, mapActionItemsToIssues } from './actionItemUtils';
import { getClosestTableFromSelection, getRowIndicesWithContent } from './tableUtils';

type ConvertSelectionToLegacyFormat = (
	contentRef: HTMLElement | null | undefined,
	range: Range | undefined,
	contentId: string,
) => {
	searchText: {
		numMatches: number;
		index: number;
		selectedText: string;
	};
};

export enum BulkSelectionResult {
	None = 'none',
	Table = 'table',
	ActionItems = 'actionItems',
}

export type PrepareBulkCreateArgs = {
	actions: IssueCreateSidePanelActions;
	activeSelection: Selection | null | undefined;
	contentId: string;
	container: HTMLElement | null;
	lastModified?: string | null;
	pageMode?: PageMode;
	hasEditPermission: boolean;
	setIsCreatedFromTable: (v: boolean) => void;
	setIsCreatedFromActionItems: (v: boolean) => void;
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat;
};

/**
 * Prepares the bulk create button from selection by setting the necessary context and actions. Used in both the renderer (@highlight-actions) and editor (@jira-work-create-extension)
 */
export const prepareBulkCreateFromSelection = ({
	actions,
	activeSelection,
	contentId,
	pageMode,
	hasEditPermission,
	container,
	lastModified,
	setIsCreatedFromTable,
	setIsCreatedFromActionItems,
	convertSelectionToLegacyFormat,
}: PrepareBulkCreateArgs): BulkSelectionResult => {
	const selectedActionList = getClosestActionListFromSelection(activeSelection);
	const selectedTable = getClosestTableFromSelection(activeSelection);

	if (
		!selectedTable &&
		(!selectedActionList || !fg('confluence-create-work-items-from-action-items'))
	) {
		// If the selection is not for a table or action items, we shouldn't set any state and return none
		return BulkSelectionResult.None;
	}

	void actions.setContentId(contentId);
	void actions.setPageMode(pageMode);
	void actions.setHasEditPermission(hasEditPermission);

	if (selectedTable) {
		return prepareFromTableSelection({
			actions,
			selectedTable,
			activeSelection,
			contentId,
			container,
			lastModified,
			setIsCreatedFromTable,
			setIsCreatedFromActionItems,
			convertSelectionToLegacyFormat,
		});
	}

	if (selectedActionList && fg('confluence-create-work-items-from-action-items')) {
		return prepareFromActionItemsSelection({
			actions,
			selectedActionList: selectedActionList as HTMLElement,
			contentId,
			container,
			lastModified,
			setIsCreatedFromTable,
			setIsCreatedFromActionItems,
			convertSelectionToLegacyFormat,
		});
	}

	return BulkSelectionResult.None;
};

type CommonPrepareArgs = {
	actions: IssueCreateSidePanelActions;
	contentId: string;
	container: HTMLElement | null;
	lastModified?: string | null;
	setIsCreatedFromTable: (v: boolean) => void;
	setIsCreatedFromActionItems: (v: boolean) => void;
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat;
};

const prepareFromTableSelection = ({
	actions,
	selectedTable,
	activeSelection,
	contentId,
	container,
	lastModified,
	setIsCreatedFromTable,
	setIsCreatedFromActionItems,
	convertSelectionToLegacyFormat,
}: CommonPrepareArgs & {
	selectedTable: HTMLTableElement;
	activeSelection: Selection | null | undefined;
}): BulkSelectionResult => {
	void actions.setSelectedTable(selectedTable);
	const insertStorageColumnTableRequestPayload = constructInsertStorageColumnTableRequestPayload(
		contentId,
		convertSelectionToLegacyFormat,
		getValidRange(activeSelection?.getRangeAt(0)),
		container,
		lastModified,
	);
	void actions.setInsertStorageColumnTableRequestPayload(insertStorageColumnTableRequestPayload);
	setIsCreatedFromTable(true);
	setIsCreatedFromActionItems(false);
	void actions.setSelectedActionItems(undefined);
	void actions.setInsertMultipleStorageFragmentsRequestPayload(undefined);
	return BulkSelectionResult.Table;
};

const prepareFromActionItemsSelection = ({
	actions,
	selectedActionList,
	contentId,
	container,
	lastModified,
	setIsCreatedFromTable,
	setIsCreatedFromActionItems,
	convertSelectionToLegacyFormat,
}: CommonPrepareArgs & { selectedActionList: HTMLElement }): BulkSelectionResult => {
	void actions.setSelectedActionItems(selectedActionList);
	// Skip if any child has data-task-list-local-id -> avoid double counting nested action items
	const actionItems = selectedActionList.querySelectorAll(
		'div[data-task-local-id]:not([data-task-local-id=""]):not(:has([data-task-list-local-id]))',
	);
	const ranges: Range[] = [];
	actionItems.forEach((actionItem) => {
		const range = new Range();
		range.selectNodeContents(actionItem);
		const validRange = getValidRangeForActionItem(range);
		if (validRange) {
			ranges.push(validRange);
		}
	});
	const insertMultipleStorageFragmentsRequestPayload =
		constructInsertMultipleStorageFragmentsRequestPayload(
			contentId,
			convertSelectionToLegacyFormat,
			ranges,
			container,
			lastModified,
		);
	void actions.setInsertMultipleStorageFragmentsRequestPayload(
		insertMultipleStorageFragmentsRequestPayload,
	);
	setIsCreatedFromActionItems(true);
	setIsCreatedFromTable(false);
	void actions.setSelectedTable(undefined);
	void actions.setInsertStorageColumnTableRequestPayload(undefined);
	return BulkSelectionResult.ActionItems;
};

/**
 *
 * @param isCreatedFromTable - whether the selection is from a table
 * @param isCreatedFromActionItems - whether the selection is from action items
 * @param selectedTable - the selected table
 * @param selectedActionItems - the selected action items
 * @returns the expected number of issues to be created from non-empty rows/items
 */
export const calculateExpectedIssueIndices = (
	isCreatedFromTable: boolean,
	isCreatedFromActionItems: boolean,
	selectedTable?: HTMLTableElement,
	selectedActionItems?: HTMLElement,
): number[] => {
	if (isCreatedFromTable && selectedTable) return getRowIndicesWithContent(selectedTable.rows);
	if (isCreatedFromActionItems && selectedActionItems)
		return mapActionItemsToIssues(selectedActionItems).map((issue) => issue.rowIndex ?? -1);
	return [];
};
